import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ArrowLeft, 
  Plus, 
  Trash2, 
  Edit3, 
  CheckCircle, 
  Clock, 
  AlertCircle, 
  Pause, 
  Play
} from 'lucide-react';
import { useEscapeKey } from '../hooks/useEscapeKey';
import { useAuth } from '../contexts/AuthContext';
import { Product, Feature } from '../types';
import { getProducts, updateProduct } from '../firebase/firestore';

interface RoadmapsProps {
  boardId: string;
  isOpen: boolean;
  onClose: () => void;
}

const Roadmaps: React.FC<RoadmapsProps> = ({ boardId, isOpen, onClose }) => {
  const { user } = useAuth();
  const [products, setProducts] = useState<Product[]>([]);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!isOpen || !user) {
      setProducts([]);
      setIsLoading(false);
      return;
    }
    loadProducts();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- loadProducts is stable
  }, [boardId, user, isOpen]);

  // Add escape key functionality
  useEscapeKey(onClose, isOpen);

  const loadProducts = async () => {
    try {
      setIsLoading(true);
      const productsData = await getProducts(boardId);
      setProducts(productsData);
    } catch (error) {
      console.error('Error loading products:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const getStatusColor = (status: Feature['status']) => {
    switch (status) {
      case 'Not Started':
        return 'text-gray-600 bg-gray-100';
      case 'In Progress':
        return 'text-blue-600 bg-blue-100';
      case 'Completed':
        return 'text-green-600 bg-green-100';
      case 'Blocked':
        return 'text-red-600 bg-red-100';
      case 'On Hold':
        return 'text-yellow-600 bg-yellow-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  const getStatusIcon = (status: Feature['status']) => {
    switch (status) {
      case 'Not Started':
        return <Clock size={16} />;
      case 'In Progress':
        return <Play size={16} />;
      case 'Completed':
        return <CheckCircle size={16} />;
      case 'Blocked':
        return <AlertCircle size={16} />;
      case 'On Hold':
        return <Pause size={16} />;
      default:
        return <Clock size={16} />;
    }
  };

  const handleFeatureStatusChange = (featureId: string, newStatus: Feature['status']) => {
    if (!selectedProduct) return;

    const updatedFeatures = selectedProduct.features.map(feature =>
      feature.id === featureId
        ? { ...feature, status: newStatus, updatedAt: new Date() }
        : feature
    );

    setSelectedProduct({
      ...selectedProduct,
      features: updatedFeatures
    });
  };

  const handleAddFeature = () => {
    if (!selectedProduct) return;

    const newFeature: Feature = {
      id: `feature_${Date.now()}`,
      name: '',
      status: 'Not Started',
      description: '',
      createdAt: new Date(),
      updatedAt: new Date()
    };

    setSelectedProduct({
      ...selectedProduct,
      features: [...selectedProduct.features, newFeature]
    });
  };

  const handleRemoveFeature = (featureId: string) => {
    if (!selectedProduct) return;

    setSelectedProduct({
      ...selectedProduct,
      features: selectedProduct.features.filter(f => f.id !== featureId)
    });
  };

  const handleFeatureNameChange = (featureId: string, newName: string) => {
    if (!selectedProduct) return;

    const updatedFeatures = selectedProduct.features.map(feature =>
      feature.id === featureId
        ? { ...feature, name: newName, updatedAt: new Date() }
        : feature
    );

    setSelectedProduct({
      ...selectedProduct,
      features: updatedFeatures
    });
  };

  const handleSaveChanges = async () => {
    if (!selectedProduct) return;

    try {
      await updateProduct(selectedProduct.id, {
        features: selectedProduct.features
      });
      setIsEditing(false);
      await loadProducts(); // Refresh the products list
    } catch (error) {
      console.error('Error saving changes:', error);
    }
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    // Reset to original product data
    const originalProduct = products.find(p => p.id === selectedProduct?.id);
    if (originalProduct) {
      setSelectedProduct(originalProduct);
    }
  };

  if (isLoading) {
    return (
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-white/15 backdrop-blur-xl z-50 flex items-center justify-center p-4"
            onClick={onClose}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              className="glass-effect rounded-3xl p-6 w-full max-w-6xl max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-center h-64">
                <div className="text-gray-600">Loading products...</div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    );
  }

  if (selectedProduct) {
    return (
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-white/15 backdrop-blur-xl z-50 flex items-center justify-center p-4"
            onClick={onClose}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              className="glass-effect rounded-3xl p-6 w-full max-w-6xl max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => setSelectedProduct(null)}
              className="flex items-center space-x-2 text-gray-600 hover:text-gray-800 transition-colors"
            >
              <ArrowLeft size={20} />
              <span>Back to Products</span>
            </button>
            <div className="h-6 w-px bg-gray-300" />
            <h1 className="text-2xl font-bold text-gray-800">
              {selectedProduct.name} - Roadmap
            </h1>
          </div>
          <div className="flex items-center space-x-3">
            {isEditing ? (
              <>
                <button
                  onClick={handleCancelEdit}
                  className="btn-secondary"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveChanges}
                  className="btn-primary"
                >
                  Save Changes
                </button>
              </>
            ) : (
              <button
                onClick={() => setIsEditing(true)}
                className="btn-primary flex items-center space-x-2"
              >
                <Edit3 size={16} />
                <span>Edit Features</span>
              </button>
            )}
          </div>
        </div>

        {/* Product Info */}
        <div className="glass-effect rounded-xl p-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <label className="text-sm font-medium text-gray-600">Status</label>
              <p className="text-gray-800 font-medium">{selectedProduct.status}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-600">Priority Score</label>
              <p className="text-gray-800 font-medium">{selectedProduct.priorityScore || 'N/A'}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-600">Total Features</label>
              <p className="text-gray-800 font-medium">{selectedProduct.features.length}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-600">Completed</label>
              <p className="text-gray-800 font-medium">
                {selectedProduct.features.filter(f => f.status === 'Completed').length}
              </p>
            </div>
          </div>
        </div>

        {/* Features List */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold text-gray-800">Features</h2>
            {isEditing && (
              <button
                onClick={handleAddFeature}
                className="btn-secondary flex items-center space-x-2"
              >
                <Plus size={16} />
                <span>Add Feature</span>
              </button>
            )}
          </div>

          {selectedProduct.features.length === 0 ? (
            <div className="glass-effect rounded-xl p-8 text-center">
              <p className="text-gray-600">No features defined yet.</p>
              {isEditing && (
                <button
                  onClick={handleAddFeature}
                  className="btn-primary mt-4 flex items-center space-x-2 mx-auto"
                >
                  <Plus size={16} />
                  <span>Add First Feature</span>
                </button>
              )}
            </div>
          ) : (
            <div className="space-y-3">
              {selectedProduct.features.map((feature, index) => (
                <motion.div
                  key={feature.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="glass-effect rounded-xl p-4"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4 flex-1">
                      {isEditing ? (
                        <input
                          type="text"
                          value={feature.name}
                          onChange={(e) => handleFeatureNameChange(feature.id, e.target.value)}
                          className="flex-1 input-field"
                          placeholder="Feature name"
                        />
                      ) : (
                        <h3 className="text-lg font-medium text-gray-800 flex-1">
                          {feature.name || 'Unnamed Feature'}
                        </h3>
                      )}
                      
                      <select
                        value={feature.status}
                        onChange={(e) => handleFeatureStatusChange(feature.id, e.target.value as Feature['status'])}
                        disabled={!isEditing}
                        className={`px-3 py-1 rounded-lg text-sm font-medium border ${
                          isEditing 
                            ? 'border-gray-300 bg-white' 
                            : 'border-transparent bg-transparent'
                        } ${getStatusColor(feature.status)}`}
                      >
                        <option value="Not Started">Not Started</option>
                        <option value="In Progress">In Progress</option>
                        <option value="Completed">Completed</option>
                        <option value="Blocked">Blocked</option>
                        <option value="On Hold">On Hold</option>
                      </select>
                    </div>
                    
                    {isEditing && (
                      <button
                        onClick={() => handleRemoveFeature(feature.id)}
                        className="text-red-500 hover:text-red-700 transition-colors ml-4"
                      >
                        <Trash2 size={16} />
                      </button>
                    )}
                  </div>
                  

                  
                  <div className="flex items-center space-x-4 mt-3 text-xs text-gray-500">
                    <span>Updated: {feature.updatedAt ? new Date(feature.updatedAt).toLocaleDateString() : 'Unknown'}</span>
                    <div className="flex items-center space-x-1">
                      {getStatusIcon(feature.status)}
                      <span>{feature.status}</span>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    );
  }

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-white/15 backdrop-blur-xl z-50 flex items-center justify-center p-4"
          onClick={onClose}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            className="glass-effect rounded-3xl p-6 w-full max-w-6xl max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={onClose}
            className="flex items-center space-x-2 text-gray-600 hover:text-gray-800 transition-colors"
          >
            <ArrowLeft size={20} />
            <span>Back to Board</span>
          </button>
          <div className="h-6 w-px bg-gray-300" />
          <h1 className="text-2xl font-bold text-gray-800">Product Roadmaps</h1>
        </div>
      </div>

      {/* Products Grid */}
      {products.length === 0 ? (
        <div className="glass-effect rounded-xl p-8 text-center">
          <p className="text-gray-600">No products found in this board.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {products.map((product) => {
            const completedFeatures = product.features.filter(f => f.status === 'Completed').length;
            const totalFeatures = product.features.length;
            const progressPercentage = totalFeatures > 0 ? (completedFeatures / totalFeatures) * 100 : 0;

            return (
              <motion.div
                key={product.id}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => setSelectedProduct(product)}
                className="glass-effect rounded-xl p-6 cursor-pointer hover:bg-white/20 transition-all duration-200"
              >
                <div className="space-y-4">
                  <div>
                    <h3 className="text-lg font-bold text-gray-800">
                      {product.name}
                    </h3>
                  </div>

                  <div className="space-y-3">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-600">Progress</span>
                      <span className="font-medium text-gray-800">
                        {completedFeatures}/{totalFeatures} features
                      </span>
                    </div>
                    
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-gradient-to-r from-satsuma-500 to-heian-500 h-2 rounded-full transition-all duration-300"
                        style={{ width: `${progressPercentage}%` }}
                      />
                    </div>
                  </div>

                  <div className="flex items-center justify-between text-sm">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(product.status as any)}`}>
                      {product.status}
                    </span>
                    <span className="text-gray-500">
                      Priority: {product.priorityScore || 'N/A'}
                    </span>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default Roadmaps;
